import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  PersonAdd,
  PersonRemove
} from '@mui/icons-material';
import { membersApi } from './api';

const GroupMembers = ({
  group,
  showNotification,
  fetchGroups,
  setGroups,
  groups,
  setSelectedGroup
}) => {
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const [newMemberData, setNewMemberData] = useState({
    name: '',
    phone: '',
    email: '',
    location: '',
    id_number: ''
  });

  // Fetch members when group changes
  useEffect(() => {
    if (group && group.id) {
      fetchMembers(group.id);
    }
  }, [group]);

  // Function to fetch members for the current group
  const fetchMembers = async (groupId) => {
    if (!groupId) return;

    try {
      setLoadingMembers(true);
      const data = await membersApi.getMembers(groupId);
      setMembers(data);
    } catch (err) {
      console.error('Error fetching members:', err);
      showNotification('Failed to load members', 'error');
    } finally {
      setLoadingMembers(false);
    }
  };

  // Function to add a new member
  const handleAddMember = async () => {
    if (!group) return;

    try {
      setLoading(true);
      await membersApi.addMember(group.id, newMemberData);
      setAddMemberDialog(false);
      setNewMemberData({
        name: '',
        phone: '',
        email: '',
        location: '',
        id_number: ''
      });

      // Refresh the members list
      await fetchMembers(group.id);

      showNotification('Member added successfully!', 'success');
    } catch (err) {
      console.error('Error adding member:', err);
      showNotification('Failed to add member', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" color="primary">Group Members</Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<PersonAdd />}
              onClick={() => {
                setAddMemberDialog(true);
              }}
              disabled={!group}
              sx={{ mr: 2 }}
            >
              Add Member
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<PersonRemove />}
              disabled={!group}
            >
              Remove Member
            </Button>
          </Box>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Name</Typography></Grid>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Member ID</Typography></Grid>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Unallocated Funds</Typography></Grid>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Savings</Typography></Grid>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Loans</Typography></Grid>
          <Grid item xs={2}><Typography variant="subtitle2" fontWeight="bold">Advance Loans</Typography></Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />

        {loadingMembers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : members && members.length > 0 ? (
          members.map((member) => (
            <Grid container key={member.id} spacing={2} sx={{ mb: 2, '&:hover': { bgcolor: 'background.default'} }}>
              <Grid item xs={2}><Typography>{member.name}</Typography></Grid>
              <Grid item xs={2}><Typography>{member.member_id}</Typography></Grid>
              <Grid item xs={2}>
                <Typography>
                  {formatCurrency(member.unallocated_funds || 0)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>
                  {formatCurrency(member.savings_balance || 0)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>
                  {formatCurrency(member.loan_balance || 0)}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>
                  {formatCurrency(member.advance_loan_balance || 0)}
                </Typography>
              </Grid>
            </Grid>
          ))
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">No members found for this group.</Typography>
          </Box>
        )}
      </Paper>

      {/* Add Member Dialog */}
      <Dialog
        open={addMemberDialog}
        onClose={() => setAddMemberDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Member</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Member Name"
              fullWidth
              value={newMemberData.name}
              onChange={(e) => setNewMemberData({...newMemberData, name: e.target.value})}
              required
            />
            <TextField
              label="Phone Number"
              fullWidth
              value={newMemberData.phone}
              onChange={(e) => setNewMemberData({...newMemberData, phone: e.target.value})}
              required
            />
            <TextField
              label="Email"
              fullWidth
              type="email"
              value={newMemberData.email}
              onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
            />
            <TextField
              label="Location"
              fullWidth
              value={newMemberData.location}
              onChange={(e) => setNewMemberData({...newMemberData, location: e.target.value})}
            />
            <TextField
              label="ID Number"
              fullWidth
              value={newMemberData.id_number}
              onChange={(e) => setNewMemberData({...newMemberData, id_number: e.target.value})}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={!newMemberData.name || !newMemberData.phone || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupMembers;